import cx from "classnames";
import { ReactNode, useCallback, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import { PressEvent } from "react-aria";

import useMainNavigationStore from "components/MainNavigation/stores/useMainNavigationStore";
import Box from "ds/components/Box";
import { type MainNavigationTransition } from "components/MainNavigation/hooks/useFadeTransition";
import Icon from "ds/components/Icon";
import { ChevronRight } from "components/icons/generated";
import useMainNavigationActiveStates from "components/MainNavigation/hooks/useActiveStates";
import { selectNavigationShowsExpandedContent } from "components/MainNavigation/selectors/navigationState";
import { selectGroupCount } from "components/MainNavigation/selectors/groupCountSelector";
import { selectIsGroupExpanded } from "components/MainNavigation/selectors/groupExpanded";
import type { MainNavigationGroupItem as MainNavigationGroupItemType } from "components/MainNavigation/types";
import MainNavigationDropdownGroupItem, {
  MainNavigationDropdownGroupItemProps,
} from "components/MainNavigation/components/elements/GroupItem/Dropdown";
import MainNavigationCounter from "components/MainNavigation/components/atoms/Counter";
import MainNavigationBaseActionButton from "components/MainNavigation/components/atoms/BaseAction/Button";
import { TooltipModalReferenceProps } from "ds/components/TooltipModal/types";
import useMainNavigationDropdownManager from "components/MainNavigation/hooks/useDropdownManager";
import useMainNavigationHoverWithDelay, {
  COLLAPSED_SIDEBAR_DROPDOWN_OPEN_DELAY,
} from "components/MainNavigation/hooks/useHoverWithDelay";

import styles from "./styles.module.css";
import MainNavigationGroupMenuItem from "./MenuItem";

export type DropdownTriggerRenderFn = (props: { onPress: (e: PressEvent) => void }) => ReactNode;

export type MainNavigationGroupItemProps = {
  group: MainNavigationGroupItemType;
  emptyState?: ReactNode;
  tooltipModalRef?: TooltipModalReferenceProps["ref"];
  dropdownOnly?: boolean;
  dropdownMenuItemsCustomRenderers?: MainNavigationDropdownGroupItemProps["menuItemsCustomRenderers"];
  dropdownTriggerComponent?: DropdownTriggerRenderFn;
  dropdownClassName?: string;
} & MainNavigationTransition;

const MainNavigationGroupItem = ({
  group,
  transitionClasses,
  emptyState,
  tooltipModalRef,
  dropdownOnly,
  dropdownMenuItemsCustomRenderers,
  dropdownTriggerComponent,
  dropdownClassName,
}: MainNavigationGroupItemProps) => {
  const { showsExpandedContent, isGroupExpanded, toggleGroupExpanded, groupCount } =
    useMainNavigationStore(
      useShallow((state) => ({
        showsExpandedContent: selectNavigationShowsExpandedContent(state),
        isGroupExpanded: selectIsGroupExpanded(state, group.id),
        toggleGroupExpanded: state.toggleGroupExpanded,
        groupCount: selectGroupCount(state, group),
      }))
    );

  const { activeGroupId } = useMainNavigationActiveStates();
  const isActive = activeGroupId === group.id;
  const hasActiveState = isActive && (!showsExpandedContent || !isGroupExpanded);
  const isEmpty = group.items.length === 0;
  const itemsContainerId = `group-${group.id}-items`;
  const showItems = showsExpandedContent && isGroupExpanded;

  const {
    isOpen: isDropdownOpen,
    openDropdown,
    toggleDropdown,
    closeDropdown,
  } = useMainNavigationDropdownManager(group.id);

  // Don't show dropdown on hover if group is already expanded inline,
  // or while the nav is collapsed (hovering it opens the preview instead)
  const shouldDisableDropdownOnHover = !showsExpandedContent || isGroupExpanded;

  const { handleMouseEnter, handleMouseLeave } = useMainNavigationHoverWithDelay({
    onOpen: openDropdown,
    onClose: closeDropdown,
    shouldDisableOpen: shouldDisableDropdownOnHover,
    openDelay: !showsExpandedContent ? COLLAPSED_SIDEBAR_DROPDOWN_OPEN_DELAY : undefined,
  });

  const handleToggleGroupExpanded = useCallback(
    (e: PressEvent) => {
      // When sidebar is collapsed or in dropdown-only mode, handle dropdown
      if (dropdownOnly || !showsExpandedContent) {
        // Keyboard interaction: toggle (open/close)
        if (e.pointerType === "keyboard") {
          toggleDropdown();
        }
        // Mouse interaction: only open if not already open
        else if (!isDropdownOpen) {
          openDropdown();
        }

        return;
      }

      // When sidebar is expanded, toggle inline expansion and close dropdown
      toggleGroupExpanded(group.id);
      closeDropdown();

      // Collapsing keeps the pointer on the trigger, so no new mouseenter re-arms the hover-open
      const isCollapsingGroup = isGroupExpanded;
      if (isCollapsingGroup && e.pointerType === "mouse") {
        handleMouseEnter();
      }
    },
    [
      dropdownOnly,
      group.id,
      toggleGroupExpanded,
      showsExpandedContent,
      closeDropdown,
      toggleDropdown,
      isDropdownOpen,
      openDropdown,
      isGroupExpanded,
      handleMouseEnter,
    ]
  );

  // Flatten group sections into a single array of items for rendering
  const flattenedItems = useMemo(
    () => group.items.flatMap((section) => section.items),
    [group.items]
  );

  const triggerButton = useMemo(
    () =>
      dropdownTriggerComponent?.({ onPress: handleToggleGroupExpanded }) || (
        <MainNavigationBaseActionButton
          id={group.htmlId}
          icon={group.icon}
          title={group.title}
          onPress={handleToggleGroupExpanded}
          isActive={hasActiveState}
          isFocused={isDropdownOpen}
          aria-label={`${group.title} - ${isGroupExpanded ? "Collapse" : "Expand"}`}
          aria-expanded={isGroupExpanded}
          aria-controls={itemsContainerId}
          transitionClasses={transitionClasses}
        >
          <Box align="center" gap="medium">
            <MainNavigationCounter
              count={groupCount}
              inversed={hasActiveState}
              className={transitionClasses}
              minimized={!showsExpandedContent}
            />

            <Icon
              className={transitionClasses}
              src={ChevronRight}
              size="default"
              rotate={isGroupExpanded ? "90" : undefined}
              withTransition
              noShrink
            />
          </Box>
        </MainNavigationBaseActionButton>
      ),
    [
      dropdownTriggerComponent,
      group.htmlId,
      group.icon,
      group.title,
      handleToggleGroupExpanded,
      hasActiveState,
      isDropdownOpen,
      isGroupExpanded,
      itemsContainerId,
      transitionClasses,
      groupCount,
      showsExpandedContent,
    ]
  );

  return (
    <Box direction="column" fullWidth>
      {/* Show dropdown menu on hover */}
      <Box
        direction="column"
        fullWidth
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={tooltipModalRef}
      >
        <MainNavigationDropdownGroupItem
          group={group}
          triggerComponent={triggerButton}
          menuItemsCustomRenderers={dropdownMenuItemsCustomRenderers}
          className={dropdownClassName}
          withGroupTitle={!showsExpandedContent}
        />
      </Box>

      {/* Expandable list on click */}
      {!dropdownOnly && (
        <Box
          direction="column"
          id={itemsContainerId}
          className={cx(styles.menuItems, showItems ? styles.expanded : styles.collapsed)}
          aria-hidden={!showItems}
          inert={!showItems || undefined}
          scrollable
        >
          <Box direction="column" gap="small" padding="small 0 0">
            {isEmpty && emptyState}
            {!isEmpty &&
              flattenedItems.map((item) => (
                <MainNavigationGroupMenuItem
                  key={item.id}
                  item={item}
                  className={styles.menuItem}
                />
              ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MainNavigationGroupItem;
